import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  Memecoin,
  MemecoinComment,
  MemecoinCommentReaction,
  MemecoinCommentReply,
  TransactionEvent,
  TwitterUserMetadata,
} from '../../types/reducerTypes'

type TransactionsMeta = {
  total: number
  poolAddress: string
}

export type MemecoinsSlice = {
  memecoins: Memecoin[]
  transactions: {
    // [cacheKey: string]: {
    data: TransactionEvent[]
    meta?: TransactionsMeta
    // }
  }
  influencers: {
    user: {
      walletAddress: string
      metadata: TwitterUserMetadata
    }
    transactions: TransactionEvent[]
  }[]
  comments: MemecoinComment[]
  memecoinsFilter?: string
  shouldRefetchCurrentMemecoin: boolean
}

const initialState: MemecoinsSlice = {
  memecoins: [],
  memecoinsFilter: '',
  transactions: {
    data: [],
  },
  influencers: [],
  comments: [],
  shouldRefetchCurrentMemecoin: false
}

export const memecoins = createSlice({
  name: 'memecoins',
  initialState,
  reducers: {
    setMemecoins: (state, { payload }: PayloadAction<{ memecoins: Memecoin[], filter: string }>) => {
      if (state.memecoinsFilter === payload.filter) {
        const existingMemecoinIds = new Set(
          state.memecoins?.map((pool) => pool._id) || []
        )

        const newMemecoins = payload.memecoins.filter(
          (pool) => !existingMemecoinIds.has(pool._id)
        )

        state.memecoins = [
          ...state.memecoins,
          ...newMemecoins,
        ]
      } else {
        state.memecoins = payload.memecoins
        state.memecoinsFilter = payload.filter
      }
    },
    // this is now smart enough to update if we need
    appendMemecoin: (state, { payload }: PayloadAction<Memecoin>) => {
      if (!state.memecoins) {
        state.memecoins = []
      }
      const memecoinIndex = state.memecoins.findIndex(
        (memecoin) => memecoin.poolAddress === payload.poolAddress
      )

      console.log({ memecoinIndex, memecoins: state.memecoins, payload })

      if (memecoinIndex !== -1) {
        state.memecoins[memecoinIndex] = payload
        return
      } else {
        state.memecoins.unshift(payload)
      }
    },
    setMemecoinComments: (
      state,
      { payload }: PayloadAction<MemecoinComment[]>,
    ) => {
      state.comments = payload
    },
    appendMemecoinComment: (
      state,
      { payload }: PayloadAction<MemecoinComment>,
    ) => {
      if (!state.comments) {
        state.comments = []
      }
      const existingComment = state.comments.some(
        (comment) => comment._id === payload._id,
      )
      if (!existingComment) {
        state.comments.unshift(payload)
      }
    },
    appendMemecoinCommentReply: (
      state,
      {
        payload,
      }: PayloadAction<{ replies: MemecoinCommentReply[]; commentId: string }>,
    ) => {
      const updatedComments = state.comments.map((comment) => {
        const { commentId, replies: incomingReplies } = payload
        if (commentId === comment._id) {
          const replyIds = comment.replies.map((reply) => reply._id)
          const filteredIncomingReply = incomingReplies.filter(
            (reply) => !replyIds.includes(reply._id),
          )
          return {
            ...comment,
            replies: [...comment.replies, ...filteredIncomingReply],
          }
        }

        return comment
      })

      state.comments = updatedComments
    },
    appendMemecoinCommentReaction: (
      state,
      {
        payload,
      }: PayloadAction<{
        reactions: MemecoinCommentReaction[]
        commentId: string
      }>,
    ) => {
      const updatedComments = state.comments.map((comment) => {
        const { commentId, reactions: incomingReactions } = payload
        if (commentId === comment._id) {
          if (comment.reactions.length < incomingReactions.length) {
            // new reaction
            const reactionIds = comment.reactions.map(
              (reaction) => reaction._id,
            )
            const filteredIncomingReactions = incomingReactions.filter(
              (reaction) => !reactionIds.includes(reaction._id),
            )
            return {
              ...comment,
              reactions: [...comment.reactions, ...filteredIncomingReactions],
            }
          } else {
            // reaction removed
            const reactionIds = incomingReactions.map(
              (reaction) => reaction._id,
            )
            const updatedReactions = comment.reactions.filter((reaction) =>
              reactionIds.includes(reaction._id),
            )
            return {
              ...comment,
              reactions: updatedReactions,
            }
          }
        }

        return comment
      })

      state.comments = updatedComments
    },
    appendMemecoinCommentReplyReaction: (
      state,
      {
        payload,
      }: PayloadAction<
        MemecoinCommentReaction & { replyId: string; isRemoved: boolean }
      >,
    ) => {
      const { replyId, isRemoved, ...reaction } = payload
      if (isRemoved) {
        const updatedComments = state.comments.map((comment) => {
          const updatedReplies = comment.replies.map((reply) => {
            if (replyId === reply._id) {
              return {
                ...reply,
                reactions: reply.reactions.filter(
                  (r) =>
                    !(
                      r.emoji === reaction.emoji &&
                      r.author.walletAddress === reaction.author.walletAddress
                    ),
                ),
              }
            }
            return reply
          })

          return {
            ...comment,
            replies: updatedReplies,
          }
        })

        state.comments = updatedComments
      } else {
        const updatedComments = state.comments.map((comment) => {
          const updatedReplies = comment.replies.map((reply) => {
            if (replyId === reply._id) {
              return {
                ...reply,
                reactions: [...reply.reactions, reaction],
              }
            }
            return reply
          })

          return {
            ...comment,
            replies: updatedReplies,
          }
        })

        state.comments = updatedComments
      }
    },
    setMemecoinTransactions: (
      state,
      {
        payload,
      }: PayloadAction<{
        // cacheKey: string
        transactions: TransactionEvent[]
        meta: TransactionsMeta
      }>,
    ) => {
      const existingTransactionIds = new Set(
        state.transactions.data?.map((txn) => txn._id) || []
      )

      const newTransactions = payload.transactions.filter(
        (txn) => !existingTransactionIds.has(txn._id)
      )

      if (state.transactions.meta && state.transactions.meta.poolAddress === payload.meta.poolAddress) {
        state.transactions = {
          data: [...(state.transactions.data ?? []), ...newTransactions],
          meta: payload.meta,
        }
      } else {
        state.transactions = {
          data: newTransactions,
          meta: payload.meta,
        }
      }
    },
    insertTransaction: (
      state,
      {
        payload,
      }: PayloadAction<{ transaction: TransactionEvent }>,
    ) => {
      if (!state.transactions) {
        state.transactions = {
          data: [],
        }
      }
      if (!state.transactions.data) {
        state.transactions = {
          data: [],
        }
      }
      state.transactions.data.unshift(payload.transaction)
    },
    appendTransaction: (
      state,
      {
        payload,
      }: PayloadAction<{ transaction: TransactionEvent }>,
    ) => {
      if (!state.transactions) {
        state.transactions = {
          data: [],
        }
      }
      // const cacheKey = payload.cacheKey
      if (!state.transactions.data) {
        state.transactions = {
          data: [],
        }
      }
      state.transactions.data.push(payload.transaction)
    },
    setInfluencers: (
      state,
      {
        payload,
      }: PayloadAction<
        {
          user: { walletAddress: string; metadata: TwitterUserMetadata }
          transactions: TransactionEvent[]
        }[]
      >,
    ) => {
      state.influencers = payload
    },
    setShouldRefetchCurrentMemecoin: (state, { payload }: PayloadAction<boolean>) => {
      state.shouldRefetchCurrentMemecoin = payload
    }
  },
})

export const {
  setMemecoins,
  appendMemecoin,
  setMemecoinComments,
  appendMemecoinComment,
  setMemecoinTransactions,
  insertTransaction,
  appendTransaction,
  setInfluencers,
  appendMemecoinCommentReply,
  appendMemecoinCommentReaction,
  appendMemecoinCommentReplyReaction,
  setShouldRefetchCurrentMemecoin
} = memecoins.actions

export default memecoins.reducer
